import React from 'react';
import { Loader2, Upload, FileSearch, Database, CheckCircle } from 'lucide-react';

export type OCRPhase =
  | 'validating'
  | 'uploading'
  | 'processing'
  | 'extracting'
  | 'complete';

interface OCRProgressIndicatorProps {
  phase: OCRPhase;
  progress?: number; // 0-100
}

const phases: Record<
  OCRPhase,
  {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    description: string;
  }
> = {
  validating: {
    icon: FileSearch,
    label: 'Validando',
    description: 'Verificando el archivo...',
  },
  uploading: {
    icon: Upload,
    label: 'Subiendo',
    description: 'Cargando imagen al servidor...',
  },
  processing: {
    icon: Loader2,
    label: 'Procesando',
    description: 'Analizando factura con IA...',
  },
  extracting: {
    icon: Database,
    label: 'Extrayendo',
    description: 'Obteniendo datos de la factura...',
  },
  complete: {
    icon: CheckCircle,
    label: 'Completado',
    description: 'Datos extraídos exitosamente',
  },
};

const OCRProgressIndicator: React.FC<OCRProgressIndicatorProps> = ({
  phase,
  progress,
}) => {
  const currentPhase = phases[phase];
  const Icon = currentPhase.icon;

  return (
    <div
      className="flex items-center justify-center gap-3 p-8 bg-blue-50 dark:bg-blue-900/20 rounded-lg"
      role="status"
      aria-live="polite"
      aria-busy={phase !== 'complete'}
    >
      <div className="relative">
        {phase !== 'complete' ? (
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        ) : (
          <Icon className="w-6 h-6 text-green-600" />
        )}
      </div>
      <div className="flex-1">
        <p className="font-medium text-blue-900 dark:text-blue-100">
          {currentPhase.label}
        </p>
        <p className="text-sm text-blue-600 dark:text-blue-300">
          {currentPhase.description}
        </p>
        {phase === 'processing' && (
          <p className="text-xs text-blue-500 dark:text-blue-400 mt-1">
            Esto puede tomar entre 10 y 60 segundos
          </p>
        )}
        {progress !== undefined && progress > 0 && (
          <div className="mt-2 w-full bg-blue-200 dark:bg-blue-800 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.min(progress, 100)}%` }}
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default OCRProgressIndicator;
